import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import PageState from '../components/common/PageState';
import ResultLoadingState from '../components/common/ResultLoadingState';
import {useAuth} from '../hooks/useAuth';
import {selectSubscription} from '../slices/testSlice';
import {useAppSelector} from '../hooks/redux';
import {apiClient} from '../services';
import SingleResultContent from './SingleResultContent';
import type {Test} from '../types/api';

const ProviderSingleResult: React.FC = () => {
    const {id} = useParams<{id: string}>();
    const navigate = useNavigate();
    const {user, token} = useAuth();
    const isMember = useAppSelector(selectSubscription);

    const [serverOrder, setServerOrder] = useState<Test | null>(null);
    const [serverOrderLoading, setServerOrderLoading] = useState(false);
    const [serverOrderError, setServerOrderError] = useState<string | null>(null);

    useEffect(() => {
        if (!user?.isLoggedIn) {
            return;
        }
        if (!id) return;

        let canceled = false;
        const run = async () => {
            setServerOrderError(null);
            setServerOrderLoading(true);
            try {
                const order = await apiClient.get<Test>(`/api/order/${id}`);
                if (!canceled) {
                    setServerOrder(order);
                }
            } catch (error: unknown) {
                if (canceled) return;
                const message =
                    error instanceof Error ? error.message : 'Unable to load this order. You may not have access.';
                setServerOrder(null);
                setServerOrderError(message);
            } finally {
                if (!canceled) {
                    setServerOrderLoading(false);
                }
            }
        };

        run();
        return () => {
            canceled = true;
        };
    }, [id, user?.isLoggedIn]);

    const providerNeedsFetch = !serverOrder && !serverOrderError;
    if (serverOrderLoading || providerNeedsFetch) {
        return (
            <DashboardLayout isMember={isMember}>
                <ResultLoadingState />
            </DashboardLayout>
        );
    }

    if (serverOrderError) {
        return (
            <DashboardLayout isMember={isMember}>
                <PageState
                    tone="error"
                    title="Unable to Load Order"
                    description={serverOrderError}
                    primaryAction={{label: 'Back to Provider Console', onClick: () => navigate('/provider')}}
                />
            </DashboardLayout>
        );
    }

    if (!serverOrder) {
        return (
            <DashboardLayout isMember={isMember}>
                <PageState
                    title="Result Not Found"
                    description="The requested test result could not be found."
                    primaryAction={{label: 'Back to Provider Console', onClick: () => navigate('/provider')}}
                />
            </DashboardLayout>
        );
    }

    const downloadToken = token || user?.token;
    return (
        <SingleResultContent
            result={serverOrder}
            realResult={serverOrder}
            isMember={isMember}
            token={downloadToken}
            backTo="/provider"
            backLabel="Back to Provider Console"
        />
    );
};

export default ProviderSingleResult;
